import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { organisations, type OrgUserRow, type UserSpaceRow } from "@/api/client";
import { ActionRow } from "@/components/ActionRow";
import { Chip } from "@/components/Chip";
import { Drawer } from "@/components/Drawer";
import { ErrorNote } from "@/components/ErrorNote";
import { Icon, type IconName } from "@/components/icons";
import { SearchInput, Select } from "@/components/inputs";
import { Field } from "@/components/Field";
import { ROLE_LABEL, orgQk } from "@/modules/organisations/data";

/** One person, and everything an admin may do to them.
 *
 *  Dattatray listed what was missing — *"reset MFA, send password reset link,
 *  send UDS tool link (whichever tool is applicable to that company and hence
 *  the user!)"* — and then, shown the rest of the gap, *"implement/port all in
 *  orbit."* Orbit could create a person and list people. Nothing else.
 *
 *  A DRAWER, because it is detail over a list (the module UI grammar) and
 *  because every action here needs the row's context to be safe: what the
 *  refusals say depends on this person's role, their organisation and whether
 *  they have ever signed in.
 *
 *  THE REFUSALS ARE THE FEATURE. Four of these actions can lock somebody out
 *  permanently, so the server refuses any that would leave the platform or an
 *  organisation with no active admin, and this shows the sentence it gives
 *  back rather than a generic failure. That is why nothing here is disabled
 *  optimistically: the console does not re-derive the floor, it asks.
 *
 *  NO PASSWORD IS EVER SHOWN (SEC-05). "Send a reset link" emails the person a
 *  link to set their own; there is no version of this screen that reveals a
 *  credential, because none is created.
 *
 *  THE TOOL LIST IS DERIVED, never picked. `userTools` answers which tools this
 *  person may be sent, from what their organisation holds, which spaces they
 *  may enter, and which releases are published — so an empty list is an answer
 *  and it comes with the reason.
 */
/** One row of the Security / Tools lists: what it is, what state it is in,
 *  and the one thing you can do about it.
 *
 *  THE STATE COLUMN IS THE POINT, and it is what the first version of this
 *  drawer was missing. Fabric's edit-user puts it on every row — "Enrolled
 *  2026-03-04", "Not enrolled · awaiting first login", "Sessions + old
 *  password will be invalidated" — so you can see WHY an action is offered
 *  before pressing it. A bare strip of buttons makes you press one to find
 *  out.
 */
function Row({ icon, title, hint, state, children }: {
  icon: IconName; title: string; hint?: string;
  state?: React.ReactNode; children?: React.ReactNode;
}) {
  return (
    <div style={{ display: "grid",
      gridTemplateColumns: "22px minmax(150px,1.1fr) minmax(0,1.4fr) auto",
      gap: 10, alignItems: "center", padding: "8px 0",
      borderTop: "1px solid var(--line)" }}>
      <Icon name={icon} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {hint && <div className="cap muted">{hint}</div>}
      </div>
      <div className="cap" style={{ minWidth: 0 }}>{state}</div>
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        {children}
      </div>
    </div>
  );
}

/** The DERIVED state of an account, which is not the same as the admin's
 *  switch and was conflated with it before.
 *
 *  Fabric shows both: a read-only badge for what is actually true right now
 *  (pending setup / locked / deactivated / active) and a separate writable
 *  "allow login". They differ in the case that matters — a person can be set
 *  to `active` and still be unable to sign in because they never verified
 *  their address, and one control showing "active" says the opposite.
 */
function AccountState({ u }: { u: OrgUserRow }) {
  if (u.status === "deactivated") return <Chip tone="mute">deactivated</Chip>;
  if (u.status === "suspended") return <Chip tone="warn">suspended</Chip>;
  if (!u.email_verified) return <Chip tone="warn">pending setup</Chip>;
  return <Chip tone="ok">active</Chip>;
}


export function PersonDrawer({ user, onClose }: {
  user: OrgUserRow; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sent, setSent] = useState("");

  // ONE FORM, SAVED ONCE — Fabric's edit-user shape, in the drawer rather
  // than a modal (*"we do not want form or pop up, we want that in side
  // drawer"*). The first version fired a request the moment a dropdown
  // changed, which applies a mis-click before you can take it back.
  const [f, setF] = useState({
    name: user.name, role: user.role, status: user.status,
  });
  const dirty = f.name !== user.name || f.role !== user.role
    || f.status !== user.status;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: orgQk.list });
    if (user.organisation_id) {
      void qc.invalidateQueries({ queryKey: orgQk.detail(user.organisation_id) });
    }
    void qc.invalidateQueries({ queryKey: ["organisations", "users"] });
  };

  const spaces = useQuery({
    queryKey: ["organisations", "users", user.user_id, "spaces"],
    queryFn: () => organisations.userSpaces(user.user_id),
    staleTime: 30_000, refetchOnWindowFocus: false,
  });
  const tools = useQuery({
    queryKey: ["organisations", "users", user.user_id, "tools"],
    queryFn: () => organisations.userTools(user.user_id),
    staleTime: 30_000, refetchOnWindowFocus: false,
  });

  const save = useMutation({
    // `login_id` is sent UNCHANGED so the server sees no edit. The address is
    // the identity and this screen does not offer to move it.
    mutationFn: () => organisations.updateProfile(user.user_id, {
      ...f, login_id: user.login_id }),
    onSuccess: () => { setSent("Saved."); refresh(); },
  });
  const reset = useMutation({
    mutationFn: () => organisations.sendPasswordReset(user.user_id),
    onSuccess: (r) => {
      setSent(r.invite
        ? `Setup link sent to ${r.to} — they choose their own password from it.`
        : `Reset link sent to ${r.to}. Their sessions have ended.`);
      refresh();
    },
  });
  const mfa = useMutation({
    mutationFn: () => organisations.resetMfa(user.user_id),
    onSuccess: () => {
      setSent("Two-factor and every backup code cleared. They enrol again at next sign-in.");
      refresh();
    },
  });
  const drop = useMutation({
    mutationFn: () => organisations.deleteUser(user.user_id),
    onSuccess: () => { refresh(); onClose(); },
  });
  const tool = useMutation({
    mutationFn: (t: { kind: string; tool_slug: string }) =>
      organisations.sendToolLink(user.user_id, t.kind, t.tool_slug),
    onSuccess: (r) => { setSent(`${r.product} ${r.tool} ${r.version} sent to ${r.to}.`); },
  });
  const grants = useMutation({
    mutationFn: (ids: string[]) => organisations.setUserSpaces(user.user_id, ids),
    onSuccess: () => {
      setSent("Space access updated.");
      void qc.invalidateQueries({
        queryKey: ["organisations", "users", user.user_id, "spaces"] });
    },
  });

  const err = save.error ?? reset.error ?? mfa.error ?? drop.error
    ?? tool.error ?? grants.error;
  const busy = save.isPending || reset.isPending || mfa.isPending
    || drop.isPending || tool.isPending || grants.isPending;

  const rows: UserSpaceRow[] = spaces.data?.spaces ?? [];
  const granted = rows.filter((r) => r.granted).map((r) => r.cps_id);
  // ADMINS IGNORE PER-USER GRANTS — `user_space_access` says so in its own
  // words: *"admins ignore this; they get every space in scope."* Showing an
  // admin an empty checkbox list reads as "no access", which is the opposite
  // of the truth. It also hid a real bug: eligibility required a grant row,
  // so an admin could never be sent a tool link at all.
  const isAdmin = user.role === "super_admin" || user.role === "company_admin";

  return (
    <Drawer onClose={onClose} width="min(660px,96vw)" header={
      <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
        <span style={{ fontWeight: 700 }}>{user.name}</span>
        <span className="mono cap">{user.login_id}</span>
        <AccountState u={user} />
      </span>
    }>
      {sent && <div className="panel panel-pad cap" style={{
        marginBottom: 10, borderColor: "var(--ok)",
        background: "color-mix(in srgb, var(--ok) 8%, transparent)",
      }}>{sent}</div>}
      <ErrorNote panel error={err} />

      {/* ---------------------------------------------------------- profile -- */}
      <div className="section-label flush">Profile</div>
      <div className="panel panel-pad">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
          gap: 12, alignItems: "end" }}>
          <Field label="Full name">
            <SearchInput className="fill" value={f.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setF({ ...f, name: e.target.value })} />
          </Field>
          <Field label="Email — this is their sign-in">
            {/* READ-ONLY. Dattatray: *"user should not be able to change their
                address!"*
                
                The address is not a profile field, it is the IDENTITY: it is
                the login, the recovery channel and what every audit row means
                by "who". Changing it turns one account into a different
                person's account in place, and the old holder keeps every
                session, grant and history that went with it.
                
                Fabric allows the edit; Orbit does not, and this is one of the
                places the two deliberately differ. To move somebody to a new
                address, create the new account and deactivate the old one —
                which leaves a record of both, where an in-place rename leaves
                none. */}
            <span className="mono" style={{ padding: "8px 0", display: "block" }}
              title="The address is the account's identity — create a new account to move somebody">
              {user.login_id}
            </span>
          </Field>
          <Field label="Role">
            <Select value={f.role} className="fill"
              onChange={(e) => setF({ ...f, role: e.target.value })}>
              {Object.entries(ROLE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Select>
          </Field>
          <Field label="Allow sign-in">
            {/* THE WRITABLE SWITCH, separate from the badge in the header. A
                person can be `active` and still unable to sign in because
                they never verified their address — one control showing
                "active" would say the opposite. */}
            <Select value={f.status} className="fill"
              onChange={(e) => setF({ ...f, status: e.target.value })}>
              <option value="active">Yes</option>
              <option value="suspended">No — suspended</option>
              <option value="deactivated">No — deactivated</option>
            </Select>
          </Field>
        </div>
        <ActionRow>
          <button className="btn red" disabled={!dirty || busy}
            onClick={() => setF({ name: user.name, role: user.role,
              status: user.status })}>Discard</button>
          <button className="btn teal" disabled={!dirty || busy}
            onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save changes"}
          </button>
        </ActionRow>
      </div>

      {/* --------------------------------------------------------- security -- */}
      <div className="section-label">Security</div>
      <div className="panel panel-pad">
        <Row icon="restore" title={user.email_verified ? "Reset password" : "Account setup"}
          state={user.email_verified ? null : <Chip tone="warn">pending setup</Chip>}>
          <button className="btn teal" disabled={busy}
            onClick={() => reset.mutate()}>
            {user.email_verified ? "Send reset link" : "Resend setup link"}
          </button>
        </Row>

        <Row icon="hide" title="Two-factor"
          state={user.mfa
            ? <Chip tone="ok">enrolled</Chip>
            : <Chip tone="mute">not enrolled</Chip>}>
          <button className="btn red" disabled={busy || !user.mfa}
            title={user.mfa ? undefined : "Nothing to reset — they have not enrolled"}
            onClick={() => mfa.mutate()}>Reset two-factor</button>
        </Row>
      </div>

      {/* ----------------------------------------------------- space access -- */}
      <div className="section-label">Product and space access</div>
      <div className="panel panel-pad">
        {isAdmin ? (
          <div className="cap" style={{ lineHeight: 1.6 }}>
            <b>{ROLE_LABEL[user.role] ?? user.role}</b> — implicit access to
            every space {user.role === "super_admin"
              ? "of every organisation"
              : `allocated to ${user.organisation_id}`}. Per-user grants apply
            to the <span className="mono">company_user</span> role only;
            demote them if this account needs scoping to particular spaces.
          </div>
        ) : rows.length === 0 ? (
          <span className="cap">
            {spaces.isLoading ? "Loading…"
              : "This organisation holds no product spaces yet, so there is nothing to grant."}
          </span>
        ) : (
          <>
            {rows.map((r) => (
              <label key={r.cps_id} style={{ display: "flex", gap: 10,
                alignItems: "center", padding: "3px 0" }}>
                <input type="checkbox" checked={r.granted} disabled={busy}
                  onChange={(e) => grants.mutate(
                    e.target.checked
                      ? [...granted, r.cps_id]
                      : granted.filter((c) => c !== r.cps_id))} />
                <span className="mono cap" style={{ minWidth: 90 }}>{r.kind}</span>
                <span>{r.space}</span>
                {!r.enabled && <Chip tone="mute">disabled</Chip>}
              </label>
            ))}
            <div className="cap muted" style={{ marginTop: 6 }}>
              A space their organisation holds but they cannot enter is a tool
              they cannot be sent.
            </div>
          </>
        )}
      </div>

      {/* ---------------------------------------------------- tool downloads -- */}
      <div className="section-label">Tool downloads</div>
      <div className="panel panel-pad">
        {tools.data?.blocked_because ? (
          <span className="cap">{tools.data.blocked_because}</span>
        ) : (tools.data?.tools ?? []).length === 0 ? (
          <span className="cap">
            {tools.isLoading ? "Loading…"
              : "No tool is available to them yet — it needs the product allocated "
                + "to their organisation, access to a space under it, and a published release."}
          </span>
        ) : (
          (tools.data?.tools ?? []).map((t) => (
            <Row key={`${t.kind}:${t.tool_slug}`} icon="download"
              title={t.product} hint={t.tool_slug}
              state={`${t.version_name} · ${t.platform} · a 24-hour link, five downloads`}>
              <button className="btn teal" disabled={busy}
                onClick={() => tool.mutate({ kind: t.kind, tool_slug: t.tool_slug })}>
                Send download link
              </button>
            </Row>
          ))
        )}
      </div>

      {/* ----------------------------------------------------------- remove -- */}
      <div className="section-label">Remove</div>
      <div className="panel panel-pad">
        {confirmDelete ? (
          <ActionRow top={0}>
            <span>
              Delete <b>{user.login_id}</b> for good? Their audit history stays,
              but the account cannot be recovered.
            </span>
            <button className="btn ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button className="btn red" disabled={busy}
              onClick={() => drop.mutate()}>Delete permanently</button>
          </ActionRow>
        ) : (
          <ActionRow top={0}>
            <span className="cap muted">
              For somebody who has left, set sign-in to deactivated above — the
              record stays and it can be undone. Deleting is for an account
              that should never have existed.
            </span>
            <button className="btn red" disabled={busy}
              onClick={() => setConfirmDelete(true)}>
              <Icon name="delete" /> Delete
            </button>
          </ActionRow>
        )}
      </div>
    </Drawer>
  );
}
